import React, { memo, InputHTMLAttributes } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classnames from 'classnames';
import { Theme } from '@app/theme';
import { Trans } from 'react-i18next';
import { TooltipText, Chip } from '@app/core';
import { RawCheckbox } from './RawCheckbox';

const breackPointMedia = `@media (max-width: 780px)`;

const useStyles = createUseStyles<string, {}, any>((theme: Theme) => ({
  formatBlock: {
    flex: 1,
    padding: theme.marginBase * 3,

    display: 'flex',
    alignItems: 'center',

    borderRadius: theme.borderRadius.std,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: theme.colors.grayBorder,

    position: 'relative',
    '&:hover': {
      backgroundColor: theme.colors.grayBackground,
    },
    textAlign: 'left',

    maxWidth: '100%',
    minWidth: '100%',
    minHeight: theme.marginBase * 16,

    flexDirection: 'row',
    justifyContent: 'center',

    marginRight: 0,
    marginTop: theme.marginBase * 2,

    [breackPointMedia]: {
      padding: theme.marginBase * 2,
      minHeight: theme.marginBase * 14,
    },
  },
  formatBlockChecked: {
    backgroundColor: theme.colors.grayBackground,
  },
  formatBlockAlone: {
    minHeight: theme.marginBase * 20,
    cursor: 'auto',
    backgroundColor: theme.colors.grayBackground,

    borderWidth: 0,
    [breackPointMedia]: {
      minHeight: theme.marginBase * 16,
    },
  },
  formatBlockDisabled: {
    opacity: 0.5,
    cursor: 'auto',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  title: {
    ...theme.fonts.h5,
  },
  text: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    marginTop: theme.marginBase,
  },
  formatBlockCompact: {
    minHeight: 'auto',
    padding: theme.marginBase * 2,
  },
  textRadioContainer: {
    display: 'flex',
    flex: 1,
  },
  compactRadioContainer: {
    alignItems: 'center',
  },
  radioAlone: {
    opacity: 0,
  },
  chip: {
    position: 'absolute',
    top: theme.marginBase,
    right: theme.marginBase,
    left: 'auto',
  },

  horizontalIllustration: {
    display: 'block',
    maxHeight: theme.marginBase * 10,
    maxWidth: theme.marginBase * 10,
    height: '100%',
    width: 'auto',

    marginLeft: theme.marginBase * 3,

    [breackPointMedia]: {
      maxHeight: theme.marginBase * 8,
      maxWidth: theme.marginBase * 8,
      marginLeft: theme.marginBase,
    },
  },
  illustration: {
    display: 'none',
  },
  icon: {
    display: 'none',
  },
  radio: {
    position: 'relative',
    display: 'block',
    fontSize: theme.iconSize.large,
    marginRight: theme.marginBase * 2,
    top: -2,

    [breackPointMedia]: {
      marginRight: theme.marginBase,
    },
  },
  compactRadio: {
    top: 0,
  },
  littleImage: {
    maxHeight: theme.marginBase * 8,
    maxWidth: theme.marginBase * 8,
    marginLeft: theme.marginBase * 2,
  },
  mobileContainer: {
    padding: theme.marginBase * 2,
    minHeight: theme.marginBase * 14,
  },
}));

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'title'> {
  illustration?: string;
  title: React.ReactNode;
  alt?: string;
  description?: React.ReactNode;
  checked?: boolean;
  alone?: boolean;
  disabled?: boolean;
  tooltip?: string;
  horizontal?: boolean;
  mobileDesign?: boolean;
}

export const IllustratedSelectionBlock = memo(
  ({
    illustration,
    title,
    alt,
    mobileDesign = false,
    description,
    disabled,
    tooltip,
    className,
    ...rest
  }: Props) => {
    const theme: Theme = useTheme();
    const classes = useStyles({ theme });

    return (
      <TooltipText title={tooltip} placement="top">
        <label
          className={classnames(classes.formatBlock, className, {
            [classes.formatBlockCompact]: !description,
            [classes.formatBlockDisabled]: disabled,
            [classes.mobileContainer]: mobileDesign,
          })}
        >
          {illustration && (
            <img
              src={illustration}
              alt={alt}
              className={classnames(classes.illustration, {
                [classes.compactIllustration]: !description,
              })}
            />
          )}

          <div
            className={classnames(classes.textRadioContainer, {
              [classes.compactRadioContainer]: !description,
            })}
          >
            <RawCheckbox
              noLabel
              className={classnames(classes.radio, {
                [classes.compactRadio]: !description,
              })}
              disabled={disabled}
              {...rest}
            />

            <div className={classes.textContainer}>
              <div className={classes.title} aria-label="Label">
                {title}
              </div>
              {description && <p className={classes.text}>{description}</p>}
            </div>
          </div>

          {illustration && (
            <img
              src={illustration}
              alt={alt}
              className={classnames(classes.horizontalIllustration, {
                [classes.littleImage]: mobileDesign,
              })}
            />
          )}
        </label>
      </TooltipText>
    );
  },
);
